// @ts-nocheck
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { momentTz } from '../../../../utils/dates';
import { ReactComponent as UndoIcon } from '../../../../assets/Icons/lucideIcons/undo.svg';

import { ReactComponent as DotIcon } from '../../../../assets/Icons/DotIcon.svg';
import { formatAttributeValue } from '../../../../utils/Utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import { useDraftValuesCount } from '../../../../hooks/useDraftAutoSave';
import DocumentAttributeLink from '../ViewAttributes/DocumentAttributeLink';

export const DRAFT_EXTRA_TEXT_SX = {
  color: (theme) => theme.palette.draft.accent,
  textAlign: 'center',
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontStyle: 'normal',
  lineHeight: '140%',
};

export const NEW_VARIANT_ATTRIBUTE_LABEL_SX = {
  color: (theme) => theme.palette.attributesForm?.label,
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '18px',
};

export const NEW_VARIANT_ICON_COLOR = (theme) => theme.palette.draft.iconColor;

export const NEW_VARIANT_EXTERNAL_LABEL_INPUT_SX = {
  mt: '0px !important',
  '& .MuiInputLabel-root': { display: 'none' },
  '& .MuiOutlinedInput-root': { mt: '0px !important' },
  '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline':
    { top: 0 },
};

export const DRAFT_INPUT_SX = {
  mt: '0px !important',
  '& .MuiInputLabel-root': { display: 'none' },
  '& .MuiOutlinedInput-root': {
    mt: '0px !important',
    borderRadius: '4px',
    borderColor: 'transparent !important',
    backgroundColor: (theme) => `${theme.palette.draft.inputBg} !important`,
    color: (theme) => `${theme.palette.draft.border} !important`,
    '&:hover': { borderColor: 'transparent !important' },
    '&.Mui-focused': {
      borderColor: 'transparent !important',
      borderWidth: '1px !important',
    },
    '& input, & textarea, & .MuiInputBase-input, & .MuiAutocomplete-input': {
      backgroundColor: (theme) => `${theme.palette.draft.inputBg} !important`,
      color: (theme) => `${theme.palette.draft.border} !important`,
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '21px',
      WebkitTextFillColor: (theme) =>
        `${theme.palette.draft.border} !important`,
    },
    '&.Mui-disabled input, &.Mui-disabled textarea, &.Mui-disabled .MuiInputBase-input':
      {
        WebkitTextFillColor: (theme) =>
          `${theme.palette.draft.border} !important`,
      },
    '& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline':
      {
        border: (theme) => `1px solid ${theme.palette.draft.border}`,
        top: 0,
      },
  },
};

export const DESCRIPTIVE_DRAFT_INPUT_BORDER_SX = {
  '& .MuiOutlinedInput-root, & .MuiOutlinedInput-root:hover, & .MuiOutlinedInput-root.Mui-focused':
    {
      borderColor: (theme) => `${theme.palette.draft.border} !important`,
    },
};

export const formatDraftFieldTimestamp = (updatedAt) => {
  if (!updatedAt) return null;
  const parsed = momentTz(updatedAt);
  return parsed.isValid()
    ? parsed.format('MMMM D, YYYY · HH:mm')
    : String(updatedAt);
};

const FOOTER_BOX_SX = {
  display: 'flex',
  height: 'auto',
  minHeight: '24px',
  padding: '1px 8px',
  justifyContent: 'space-between',
  alignItems: 'center',
  alignSelf: 'stretch',
  borderRadius: '4px',
  border: (theme) => `0.5px solid ${theme.palette.grey[60]}`,
  background: (theme) => theme.palette.grey[10],
  mt: 0.5,
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const FOOTER_TEXT_SX = {
  ...DRAFT_EXTRA_TEXT_SX,
  fontWeight: 400,
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
};

const FOOTER_VALUE_SX = {
  ...DRAFT_EXTRA_TEXT_SX,
  fontWeight: 600,
  display: 'inline',
  verticalAlign: 'baseline',
  '& .MuiGrid-root': { display: 'inline', p: 0, m: 0, minWidth: 0 },
  '& .MuiGrid-container': {
    display: 'inline',
    flexWrap: 'nowrap',
    width: 'auto',
    margin: 0,
  },
  '& .MuiTypography-root': {
    display: 'inline',
    fontSize: '12px',
    lineHeight: '140%',
    color: (theme) => `${theme.palette.draft.accent} !important`,
  },
};

const REVERT_SX = {
  flexShrink: 0,
  alignSelf: 'center',
  minHeight: '0 !important',
  minWidth: '0 !important',
  height: '16px !important',
  maxHeight: '16px',
  padding: '0 2px !important',
  color: 'text.secondary',
  textTransform: 'none !important',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '16px',
  '& .MuiButton-startIcon': {
    margin: '0 2px 0 0 !important',
  },
  '& .MuiSvgIcon-root': { fontSize: '12px !important', display: 'block' },
  borderRadius: '4px',
  boxShadow: 'none !important',
  '&:hover': { bgcolor: 'transparent' },
};

const createDraftRevertHandler = (onRevert) => (e) => {
  e?.preventDefault?.();
  e?.stopPropagation?.();
  onRevert?.();
};

const DraftRevertButton = ({ onRevert, sx }) => (
  <Button
    type="button"
    size="small"
    variant="text"
    startIcon={<UndoIcon width="12px" height="12px" />}
    onClick={createDraftRevertHandler(onRevert)}
    sx={{ ...REVERT_SX, ...sx }}
  >
    Revert
  </Button>
);

DraftRevertButton.propTypes = {
  onRevert: PropTypes.func.isRequired,
  sx: PropTypes.shape({}),
};

const formatFooterValue = (
  value,
  attribute,
  {
    entityId,
    entityDetailsFormikForm,
    usersList,
    groupList,
    entityType,
    associationTypes,
  }
) => {
  if (
    attribute?.input_control_type === 'TEXTFIELD_EXTENDED' &&
    typeof value === 'string'
  ) {
    return value;
  }

  // Draft comparisons show just the document name as a link, not the full
  // icon + name + preview button used in the live attribute view.
  if (attribute?.data_type === 'Document' && value) {
    return (
      <DocumentAttributeLink
        documentDetails={value}
        entityId={entityId}
        entityType={entityType}
      />
    );
  }

  return formatAttributeValue(
    value,
    attribute,
    false,
    entityId,
    entityDetailsFormikForm,
    usersList,
    groupList,
    entityType,
    associationTypes ?? EMPTY_OBJECT
  );
};

const DraftDotChip = ({ label, className }) => (
  <Chip label={label} icon={<DotIcon />} className={className} />
);

DraftDotChip.propTypes = {
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export const DraftAttributeChip = ({
  isNewVariant = false,
  draftChipTooltip,
  withTooltip = true,
}) => {
  const chip = (
    <Chip
      label={isNewVariant ? 'Draft Value' : 'Draft'}
      className="draft-chip"
      size="small"
      sx={{ ml: 0.75, flexShrink: 0 }}
    />
  );
  if (isNewVariant || !withTooltip || !draftChipTooltip) return chip;
  return (
    <Tooltip
      arrow
      title={
        <Typography
          variant="caption"
          component="div"
          sx={{ whiteSpace: 'pre-line' }}
        >
          {draftChipTooltip}
        </Typography>
      }
    >
      {chip}
    </Tooltip>
  );
};

DraftAttributeChip.propTypes = {
  isNewVariant: PropTypes.bool,
  draftChipTooltip: PropTypes.string,
  withTooltip: PropTypes.bool,
};

export const DraftEntityStatusBanner = ({
  draftTimestamp,
  chipClassName = 'sm-chip downstreamUpstreamCip',
}) => (
  <>
    <Chip
      label="Draft"
      color="primary"
      className={chipClassName}
      sx={{ minHeight: '22px', fontSize: '12px', fontWeight: '600' }}
      title={
        draftTimestamp
          ? `Last draft saved: ${draftTimestamp}`
          : 'You have unsaved changes'
      }
      size="small"
    />
    <Typography
      variant="caption"
      sx={{ fontStyle: 'italic', color: 'text.secondary' }}
    >
      {`Last edited by you at ${draftTimestamp}`}
    </Typography>
  </>
);

DraftEntityStatusBanner.propTypes = {
  draftTimestamp: PropTypes.string,
  chipClassName: PropTypes.string,
};

export const DraftFieldFooter = ({
  descriptive = false,
  attribute,
  originalValue,
  currentDraftValue,
  onRevert,
  entityDetailsFormikForm,
  usersList,
  groupList,
  entityId,
  entityType,
  associationTypes = EMPTY_OBJECT,
}) => {
  const ctx = {
    entityId,
    entityDetailsFormikForm,
    usersList,
    groupList,
    entityType,
    associationTypes,
  };
  const formattedOriginal = useMemo(
    () => formatFooterValue(originalValue, attribute, ctx),
    [
      originalValue,
      attribute,
      entityId,
      entityDetailsFormikForm,
      usersList,
      groupList,
      entityType,
      associationTypes,
    ]
  );
  const formattedDraft = useMemo(
    () => formatFooterValue(currentDraftValue, attribute, ctx),
    [
      currentDraftValue,
      attribute,
      entityId,
      entityDetailsFormikForm,
      usersList,
      groupList,
      entityType,
      associationTypes,
    ]
  );

  return (
    <Box sx={FOOTER_BOX_SX}>
      <Typography variant="body2" component="div" sx={FOOTER_TEXT_SX}>
        {descriptive ? (
          <>
            <Box component="span" sx={{ fontWeight: 400 }}>
              Final value:{' '}
            </Box>
            <Box
              component="span"
              sx={{ ...FOOTER_VALUE_SX, textDecoration: 'line-through' }}
            >
              {formattedOriginal}
            </Box>
            <Box component="span" sx={{ fontWeight: 400 }}>
              {' '}
              →{' '}
            </Box>
            <Box
              component="span"
              sx={{
                ...FOOTER_VALUE_SX,
                color: (theme) => `${theme.palette.draft.accent} !important`,
              }}
            >
              {formattedDraft}
            </Box>
          </>
        ) : (
          <>
            <Box component="span" sx={{ fontWeight: 400 }}>
              Original Value{' '}
            </Box>
            <Box component="span" sx={FOOTER_VALUE_SX}>
              {formattedOriginal}
            </Box>
          </>
        )}
      </Typography>
      <DraftRevertButton onRevert={onRevert} />
    </Box>
  );
};

DraftFieldFooter.propTypes = {
  descriptive: PropTypes.bool,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  originalValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.shape({}),
  ]),
  currentDraftValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array,
    PropTypes.shape({}),
  ]),
  onRevert: PropTypes.func.isRequired,
  entityDetailsFormikForm: PropTypes.shape({}),
  usersList: PropTypes.arrayOf(PropTypes.shape({})),
  groupList: PropTypes.arrayOf(PropTypes.shape({})),
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityType: PropTypes.string,
  associationTypes: PropTypes.shape({}),
};

export const DraftLastEditedText = ({ editorName }) =>
  editorName ? (
    <Typography
      variant="caption"
      component="span"
      sx={{
        ...DRAFT_EXTRA_TEXT_SX,
        fontWeight: 400,
        ml: 'auto',
        flexShrink: 0,
        whiteSpace: 'nowrap',
        pl: 1,
      }}
    >
      Last updated by{' '}
      <Box component="span" sx={{ ...DRAFT_EXTRA_TEXT_SX, fontWeight: 600 }}>
        {editorName}
      </Box>
    </Typography>
  ) : null;

DraftLastEditedText.propTypes = { editorName: PropTypes.string };

export const DraftNewVariantFooter = ({
  editorName,
  updatedAt,
  onRevert,
  sx,
}) => {
  const formattedTimestamp = formatDraftFieldTimestamp(updatedAt);
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        mt: 0.5,
        ...sx,
      }}
    >
      <Typography
        variant="caption"
        component="div"
        sx={{
          ...DRAFT_EXTRA_TEXT_SX,
          textAlign: 'left',
          fontWeight: 400,
          minWidth: 0,
          flex: 1,
          lineHeight: '16px',
        }}
      >
        Draft value — not yet submitted.
        {editorName ? (
          <>
            {' '}
            Last edited by{' '}
            <Box
              component="span"
              sx={{ ...DRAFT_EXTRA_TEXT_SX, fontWeight: 600 }}
            >
              {editorName}
            </Box>
          </>
        ) : null}
        {formattedTimestamp ? <> · {formattedTimestamp}</> : null}
      </Typography>
      {onRevert ? <DraftRevertButton onRevert={onRevert} /> : null}
    </Box>
  );
};

DraftNewVariantFooter.propTypes = {
  editorName: PropTypes.string,
  updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRevert: PropTypes.func,
  sx: PropTypes.shape({}),
};

export const DraftValuesCountChip = ({ count = 0 }) =>
  count > 0 ? (
    <DraftDotChip
      label={`${count} ${count === 1 ? 'value' : 'values'} in draft`}
      className="draft-values-count-chip"
    />
  ) : null;

DraftValuesCountChip.propTypes = { count: PropTypes.number };

export const DraftItemsStateChip = ({ visible = false }) =>
  visible ? (
    <DraftDotChip
      label="Items in draft state"
      className="draft-state-header-chip"
    />
  ) : null;

DraftItemsStateChip.propTypes = { visible: PropTypes.bool };

export const resolveDraftEditorDisplayName = (updatedBy, usersList = []) => {
  if (updatedBy == null || updatedBy === '') return null;
  if (typeof updatedBy === 'object')
    return updatedBy.username || updatedBy.user || null;
  const user =
    usersList?.find?.(
      (u) =>
        u?.username === updatedBy || String(u?.user_id) === String(updatedBy)
    ) ?? null;
  return user?.username || updatedBy;
};

export const DraftDot = ({
  attributes = EMPTY_ARRAY,
  count,
  sectionId,
  sectionName,
  sx,
  ...props
}) => {
  const draftCountFromHook = useDraftValuesCount(attributes);
  let effectiveCount = draftCountFromHook;
  if (count !== undefined) {
    effectiveCount = count;
  }

  if (!effectiveCount) return null;

  let testId = 'section-draft-dot';
  if (sectionId) {
    testId = `section-draft-dot-${sectionId}`;
  } else if (sectionName) {
    testId = `section-draft-dot-${sectionName.toLowerCase().replace(/\s+/g, '-')}`;
  }

  return (
    <Box
      component="span"
      role="status"
      aria-label={`Draft changes in ${sectionName || 'this section'}`}
      data-testid={testId}
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: (theme) => theme.palette.draft.accent,
        flexShrink: 0,
        mr: 1,
        display: 'inline-block',
        ...sx,
      }}
      {...props}
    />
  );
};

DraftDot.propTypes = {
  attributes: PropTypes.arrayOf(PropTypes.shape({})),
  count: PropTypes.number,
  sectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sectionName: PropTypes.string,
  sx: PropTypes.shape({}),
};
